import React from "react";
import { Link, useNavigate } from "react-router-dom";
import Seo from "../components/Seo.jsx";

/* Terms of Service + Privacy Policy.
   Two long-form public pages that share the same editorial chrome
   (LegalLayout). Mounted at /terms and /privacy-policy as standalone routes
   (no app shell, no sidebar). LegalFooter renders the row of links at
   the bottom of the welcome page and the settings page. */

const COMPANY_NAME = "Community Ninja";
const COMPANY_DOMAIN = "communityninja.ai";
const SUPPORT_EMAIL = `support@${COMPANY_DOMAIN}`;
const PRIVACY_EMAIL = `privacy@${COMPANY_DOMAIN}`;
const LAST_UPDATED = "May 23, 2026";

/* ---------- Shared layout ---------- */

function LegalLayout({ kind, title, lede, sections, children }) {
  const navigate = useNavigate();
  const otherKind = kind === "terms" ? "privacy" : "terms";
  const otherLabel = kind === "terms" ? "Privacy Policy" : "Terms of Service";

  return (
    <div className="legal-page screen-enter" data-kind={kind}>
      <header className="legal-top">
        <button
          type="button"
          className="legal-back"
          onClick={() => {
            if (window.history.length > 1) navigate(-1);
            else navigate("/");
          }}
          aria-label="Go back"
        >
          <window.I.ArrowLeft size={14} />
          <span>Back</span>
        </button>
        <Link to="/" className="legal-brand" aria-label={`${COMPANY_NAME} home`}>
          <span className="legal-brand-name">{COMPANY_NAME}</span>
        </Link>
      </header>

      <article className="legal-shell">
        <div className="legal-hero">
          <div className="legal-eyebrow">
            <span className="legal-eyebrow-dot" />
            <span>Legal · {kind === "terms" ? "Terms of Service" : "Privacy Policy"}</span>
          </div>
          <h1 className="legal-title">{title}</h1>
          {lede && <p className="legal-lede">{lede}</p>}
          <div className="legal-meta">
            <span>Last updated <strong>{LAST_UPDATED}</strong></span>
            <span className="legal-meta-sep">·</span>
            <span>Effective immediately for new users</span>
          </div>
        </div>

        {sections && sections.length > 0 && (
          <nav className="legal-toc" aria-label="Table of contents">
            <div className="legal-toc-label">On this page</div>
            <ol className="legal-toc-list">
              {sections.map((s, i) => (
                <li key={s.id}>
                  <a href={`#${s.id}`}>
                    <span className="legal-toc-num">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <span>{s.label}</span>
                  </a>
                </li>
              ))}
            </ol>
          </nav>
        )}

        <div className="legal-body">{children}</div>

        <footer className="legal-footer">
          <div className="legal-footer-note">
            Plain-language summary, not a substitute for legal advice. If
            something here conflicts with applicable law in your
            jurisdiction, the law wins.
          </div>
          <div className="legal-footer-links">
            <Link to={`/${otherKind}`} className="legal-footer-link">
              Read the {otherLabel}
              <window.I.ArrowRight size={12} />
            </Link>
            <Link to="/" className="legal-footer-link legal-footer-link-quiet">
              Back to {COMPANY_NAME}
            </Link>
          </div>
        </footer>
      </article>
    </div>
  );
}

function Section({ id, n, title, children }) {
  return (
    <section className="legal-section" id={id}>
      <header className="legal-section-head">
        <span className="legal-section-num">{String(n).padStart(2, "0")}</span>
        <h2 className="legal-section-title">{title}</h2>
      </header>
      <div className="legal-section-body">{children}</div>
    </section>
  );
}

/* ============================================================
   Terms of Service
   ============================================================ */

const TERMS_SECTIONS = [
  { id: "agreement", label: "Agreement" },
  { id: "service", label: "What the service does" },
  { id: "account", label: "Your account" },
  { id: "acceptable-use", label: "Acceptable use" },
  { id: "reddit", label: "Reddit & third-party platforms" },
  { id: "your-content", label: "Your content & profile data" },
  { id: "our-ip", label: "Our intellectual property" },
  { id: "ai", label: "AI-generated output" },
  { id: "fees", label: "Fees, trials & changes" },
  { id: "disclaimers", label: "Disclaimers" },
  { id: "liability", label: "Limitation of liability" },
  { id: "indemnity", label: "Indemnification" },
  { id: "termination", label: "Termination" },
  { id: "law", label: "Governing law & disputes" },
  { id: "changes", label: "Changes to these terms" },
  { id: "contact", label: "Contact" },
];

function TermsScreen() {
  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <LegalLayout
      kind="terms"
      title="Terms of Service"
      lede={`These terms cover your use of ${COMPANY_NAME} — what we offer, what we expect from you, and the limits of what we can promise. They form a binding agreement, so please read them.`}
      sections={TERMS_SECTIONS}
    >
      <Seo
        title="Terms of Service"
        description={`The terms that govern your use of ${COMPANY_NAME}.`}
      />

      <Section n={1} id="agreement" title="The agreement">
        <p>
          These Terms of Service (the “Terms”) are a contract between you and{" "}
          {COMPANY_NAME} (“we,” “us,” or “our”). By creating an account,
          accessing the website at <strong>{COMPANY_DOMAIN}</strong>, or using
          the Community Ninja Chrome Extension or any feature of the service
          (collectively, the “Service”), you agree
          to be bound by these Terms and by our{" "}
          <Link to="/privacy-policy">Privacy Policy</Link>.
        </p>
        <p>
          If you are using the Service on behalf of an organisation, you
          represent that you have authority to bind that organisation and that
          “you” in these Terms refers to that organisation. If you do not
          agree to these Terms, you may not use the Service.
        </p>
      </Section>

      <Section n={2} id="service" title="What the service does">
        <p>
          {COMPANY_NAME} is a discovery tool for founders and operators. You
          describe your business once, and we monitor public Reddit
          conversations on a schedule (and on demand) to surface posts and
          comments that match your product, customers, and pain points. Each
          match is scored, ranked, and accompanied by a short rationale you
          can use to decide whether to engage.
        </p>
        <p>
          The Service is a <strong>read-only</strong> assistant by design. We
          do not post, comment, vote, send direct messages, or take any
          action on Reddit on your behalf. Whether and how to engage with any
          conversation is entirely your decision.
        </p>
      </Section>

      <Section n={3} id="account" title="Your account">
        <p>
          You need an account to use most of the Service. We use a
          third-party authentication provider; by signing in you also agree
          to that provider’s terms. You are responsible for safeguarding your
          credentials and for all activity that occurs under your account.
          Notify us promptly at <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>{" "}
          if you suspect unauthorised access.
        </p>
        <p>
          You must be at least 13 years old to use the Service (or older
          where required by local law). If you are accessing the Service on
          behalf of an employer or client, you must have authority to do so.
        </p>
      </Section>

      <Section n={4} id="acceptable-use" title="Acceptable use">
        <p>You agree not to use the Service to:</p>
        <ul>
          <li>
            Violate any law, regulation, or the rights of another person —
            including privacy, publicity, or intellectual-property rights.
          </li>
          <li>
            Harass, defraud, deceive, or send spam to any person on Reddit or
            elsewhere, including by using insights from the Service to mass
            message or astroturf.
          </li>
          <li>
            Reverse-engineer, decompile, scrape, or attempt to extract the
            source code, algorithms, or training data underlying the Service,
            except to the extent applicable law expressly permits.
          </li>
          <li>
            Use the Service to build a competing product, train a competing
            model, or aggregate data for resale.
          </li>
          <li>
            Probe, scan, overload, or disrupt the Service, our infrastructure,
            or any platform we connect to (including Reddit).
          </li>
          <li>
            Circumvent rate limits, access controls, or paid features, or
            create accounts in bulk by automated means.
          </li>
        </ul>
        <p>
          We may suspend or terminate accounts that repeatedly violate
          acceptable use, and we reserve the right to refuse service to anyone
          for any lawful reason.
        </p>
      </Section>

      <Section n={5} id="reddit" title="Reddit and third-party platforms">
        <p>
          The Service queries Reddit using Reddit’s public API. Your use of
          the Service does <em>not</em> grant you any rights in Reddit or its
          content. When you click through to Reddit from the Service, your
          interaction with that site is governed by{" "}
          <a href="https://www.redditinc.com/policies/user-agreement" target="_blank" rel="noreferrer noopener">Reddit’s User Agreement</a>{" "}
          and the rules of each subreddit.
        </p>
        <p>
          You are solely responsible for ensuring that any post, comment, or
          message you send on Reddit complies with Reddit’s site-wide rules,
          subreddit-specific rules, and applicable law. The Service does not
          authorise or excuse spam, ban evasion, vote manipulation, paid
          promotion without disclosure, or any other prohibited behaviour.
        </p>
        <p>
          The Service may also rely on other third-party providers (for
          authentication, hosting, email delivery, analytics, and AI
          inference). Their terms apply alongside ours; see our{" "}
          <Link to="/privacy-policy">Privacy Policy</Link> for the current list.
        </p>
      </Section>

      <Section n={6} id="your-content" title="Your content and profile data">
        <p>
          When you describe your business, set keywords, or provide feedback
          on matches, you give us a worldwide, royalty-free licence to host,
          process, transmit, and use that information solely to operate,
          secure, and improve the Service for you. You retain ownership of
          everything you submit.
        </p>
        <p>
          You confirm that you have the rights to provide any content you
          submit and that doing so doesn’t violate any law, contract, or
          third-party right. We do not claim any ownership of public Reddit
          content surfaced through the Service — it remains the property of
          its authors.
        </p>
      </Section>

      <Section n={7} id="our-ip" title="Our intellectual property">
        <p>
          The Service, including its software, design, text, graphics, and
          the {COMPANY_NAME} name and logos, is owned by us or our licensors
          and is protected by intellectual-property laws. We grant you a
          limited, non-exclusive, non-transferable, revocable licence to use
          the Service in accordance with these Terms. No other rights are
          granted by implication or otherwise.
        </p>
        <p>
          Feedback you send us about the Service is non-confidential, and we
          may use it without obligation to you.
        </p>
      </Section>

      <Section n={8} id="ai" title="AI-generated output">
        <p>
          The Service uses large language models to score relevance, draft
          rationales, and suggest reply language. AI output can be wrong,
          incomplete, or biased. You are responsible for reviewing any AI
          output before relying on it, and especially before publishing it
          anywhere. Do not treat AI rationales as legal, financial, medical,
          or other professional advice.
        </p>
        <p>
          You may not use AI output from the Service to develop, train, or
          improve any model that competes with the Service.
        </p>
      </Section>

      <Section n={9} id="fees" title="Fees, trials, and changes to the Service">
        <p>
          The Service is currently offered on a free basis while in early
          access. We may introduce paid tiers, change pricing, or limit free
          usage in the future; if we do, we will give you reasonable advance
          notice and you can choose whether to continue.
        </p>
        <p>
          We are constantly improving the Service. We may add, change, or
          remove features at any time. We try not to break things, but for
          features clearly marked as experimental or beta, breakage is part
          of the deal.
        </p>
      </Section>

      <Section n={10} id="disclaimers" title="Disclaimers">
        <p>
          The Service is provided <strong>“as is” and “as available.”</strong>{" "}
          To the maximum extent permitted by law, we disclaim all warranties,
          express or implied, including merchantability, fitness for a
          particular purpose, non-infringement, accuracy, and uninterrupted
          operation.
        </p>
        <p>
          We do not warrant that the Service will identify every relevant
          conversation, that scores are accurate, that drafts are appropriate
          for a given subreddit, or that the Service will be uninterrupted,
          error-free, or secure. You use the Service at your own risk.
        </p>
      </Section>

      <Section n={11} id="liability" title="Limitation of liability">
        <p>
          To the maximum extent permitted by law, neither {COMPANY_NAME} nor
          its officers, employees, or affiliates will be liable for any
          indirect, incidental, special, consequential, exemplary, or
          punitive damages — including lost profits, lost revenue, lost
          goodwill, lost data, or business interruption — arising out of or
          relating to your use of the Service, even if we have been advised
          of the possibility of such damages.
        </p>
        <p>
          Our total liability for all claims relating to the Service in any
          twelve-month period is limited to the greater of (a) the amount you
          paid us for the Service during that period, or (b) USD&nbsp;100.
          Some jurisdictions do not allow these limits; in those
          jurisdictions, our liability is limited to the smallest extent
          permitted by law.
        </p>
      </Section>

      <Section n={12} id="indemnity" title="Indemnification">
        <p>
          You agree to defend, indemnify, and hold harmless {COMPANY_NAME}{" "}
          and its officers, employees, and affiliates from and against any
          claim, demand, loss, or expense (including reasonable attorneys’
          fees) arising out of: (i) your use of the Service, (ii) your
          violation of these Terms, (iii) your violation of any third-party
          right (including Reddit’s rules or subreddit rules), or (iv) any
          content you submit to or post through the Service.
        </p>
      </Section>

      <Section n={13} id="termination" title="Termination">
        <p>
          You may stop using the Service at any time and delete your account
          from the Settings page. We may suspend or terminate your access if
          you violate these Terms, if your use creates risk or legal exposure
          for us, or if we discontinue the Service. On termination, the
          provisions that by their nature should survive (ownership,
          disclaimers, liability limits, indemnity, dispute resolution,
          general terms) will survive.
        </p>
      </Section>

      <Section n={14} id="law" title="Governing law and disputes">
        <p>
          These Terms are governed by the laws of the State of Delaware,
          USA, without regard to conflict-of-laws rules. The state and
          federal courts located in Delaware will have exclusive jurisdiction
          over any dispute arising from these Terms or the Service, except
          that either party may seek injunctive relief in any court of
          competent jurisdiction to protect its intellectual-property rights.
        </p>
        <p>
          Before filing a claim, you agree to try to resolve the dispute
          informally by contacting{" "}
          <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>. If we can’t
          resolve it within sixty (60) days, either of us may proceed.
        </p>
      </Section>

      <Section n={15} id="changes" title="Changes to these terms">
        <p>
          We may update these Terms from time to time. If we make material
          changes, we will notify you (for example, by email or an in-app
          notice) and update the “Last updated” date at the top of this
          page. Continued use of the Service after the changes take effect
          constitutes acceptance of the revised Terms.
        </p>
      </Section>

      <Section n={16} id="contact" title="Contact">
        <p>
          Questions about these Terms? Email{" "}
          <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>. For
          privacy-specific questions, see the{" "}
          <Link to="/privacy-policy">Privacy Policy</Link> or write to{" "}
          <a href={`mailto:${PRIVACY_EMAIL}`}>{PRIVACY_EMAIL}</a>.
        </p>
      </Section>
    </LegalLayout>
  );
}

/* ============================================================
   Privacy Policy
   ============================================================ */

const PRIVACY_SECTIONS = [
  { id: "overview", label: "Overview" },
  { id: "data-we-collect", label: "Data we collect" },
  { id: "how-we-use", label: "How we use data" },
  { id: "legal-bases", label: "Legal bases (EU/UK)" },
  { id: "sharing", label: "Sharing & service providers" },
  { id: "ai-processing", label: "AI processing" },
  { id: "reddit-data", label: "Reddit & public data" },
  { id: "cookies", label: "Cookies & local storage" },
  { id: "retention", label: "Retention" },
  { id: "security", label: "Security" },
  { id: "your-rights", label: "Your rights" },
  { id: "transfers", label: "International transfers" },
  { id: "children", label: "Children" },
  { id: "changes", label: "Changes to this policy" },
  { id: "contact", label: "Contact" },
];

function PrivacyScreen() {
  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <LegalLayout
      kind="privacy"
      title="Privacy Policy"
      lede={`We try to collect as little as possible, keep what we collect for as short as possible, and tell you exactly what's where. Here's the long version.`}
      sections={PRIVACY_SECTIONS}
    >
      <Seo
        title="Privacy Policy"
        description={`How ${COMPANY_NAME} collects, uses, and protects your data.`}
      />

      <Section n={1} id="overview" title="Overview">
        <p>
          This Privacy Policy explains what {COMPANY_NAME} (“we,” “us,” or
          “our”) does with your data when you use the website at{" "}
          <strong>{COMPANY_DOMAIN}</strong> or any feature of the Service.
          This Privacy Policy applies to our website at https://www.communityninja.ai,
          and the Community Ninja Chrome Extension.
          It applies to information we receive from you, from your use of the
          Service, and from third parties we work with.
        </p>
        <p>
          The short version:
        </p>
        <ul>
          <li>
            We collect what we need to make the Service work and keep it
            secure — nothing more.
          </li>
          <li>
            We don’t sell your data, and we don’t use it for advertising.
          </li>
          <li>
            We don’t post, comment, or send messages on Reddit on your
            behalf, and we don’t store private Reddit credentials.
          </li>
          <li>
            You can export or delete your data at any time from the Settings
            page.
          </li>
        </ul>
      </Section>

      <Section n={2} id="data-we-collect" title="Data we collect">
        <h3>Account information</h3>
        <p>
          When you create an account, our authentication provider passes us
          your basic identity (name, email, profile image, and a unique
          identifier). If you sign in with Google or Apple, we also receive
          the limited profile information those providers share.
        </p>
        <h3>Business profile and preferences</h3>
        <p>
          You create one or more business profiles describing your product,
          target customers, pain points, keywords, and avoided terms. We
          store these profiles, the search plans we generate from them, the
          subreddits you watch, and the notification preferences you set.
        </p>
        <h3>Activity data</h3>
        <p>
          We record the scans you run, the matches we surfaced, the rationales
          generated for them, and the feedback you give (relevant, not
          relevant, etc.) so we can rank future results for you and so the
          history page works.
        </p>
        <h3>Public Reddit content</h3>
        <p>
          To find matches, we ingest public posts and comments from Reddit
          via the official Reddit API. We cache only what we need to score
          and rank, and we only store the metadata necessary to link you
          back to the original thread (subreddit, post title, permalink,
          timestamps, score). We do not collect anything you, or anyone
          else, has set as private on Reddit.
        </p>
        <h3>Automatic data</h3>
        <p>
          Like most web services, our servers receive standard log data when
          you use the Service: IP address, browser type, device type, pages
          requested, referring URL, and timestamps. We use this for security,
          debugging, and aggregate analytics.
        </p>
        <h3>What we don’t collect</h3>
        <p>
          We don’t collect Reddit passwords, OAuth tokens (we don’t need them
          — we use the public read API), payment information (the Service is
          currently free), precise location, or sensitive categories of data.
        </p>
      </Section>

      <Section n={3} id="how-we-use" title="How we use data">
        <ul>
          <li>
            <strong>Operate the Service.</strong> Find and rank Reddit
            conversations that match your business profile, send your daily
            digest if you turn it on, and remember your preferences.
          </li>
          <li>
            <strong>Improve the Service.</strong> Diagnose bugs, measure
            aggregate performance, and refine ranking based on patterns
            across users (always in aggregate, never sold to anyone).
          </li>
          <li>
            <strong>Communicate with you.</strong> Send transactional emails
            (account changes, scan results, security alerts) and, if you
            opt in, your daily digest. We don’t send marketing emails
            without your consent.
          </li>
          <li>
            <strong>Keep things secure.</strong> Detect abuse, enforce our{" "}
            <Link to="/terms">Terms of Service</Link>, and meet legal
            obligations.
          </li>
        </ul>
      </Section>

      <Section n={4} id="legal-bases" title="Legal bases (EU / UK users)">
        <p>
          If you are in the European Economic Area, the United Kingdom, or
          Switzerland, our legal bases for processing personal data are:
        </p>
        <ul>
          <li>
            <strong>Contract.</strong> To deliver the Service you signed up
            for.
          </li>
          <li>
            <strong>Legitimate interests.</strong> To secure, debug, and
            improve the Service in ways you’d reasonably expect, balanced
            against your privacy.
          </li>
          <li>
            <strong>Consent.</strong> For optional features such as the
            daily digest email, where we ask you to opt in.
          </li>
          <li>
            <strong>Legal obligation.</strong> To comply with applicable
            law, court orders, or lawful requests from regulators.
          </li>
        </ul>
      </Section>

      <Section n={5} id="sharing" title="Sharing and service providers">
        <p>
          We do not sell your personal data. We share it only with the
          service providers we need to run the product, and only to the
          extent each provider needs it for that purpose. Today those
          providers include:
        </p>
        <ul>
          <li>
            <strong>Authentication.</strong> Clerk — to manage sign-in and
            sessions.
          </li>
          <li>
            <strong>Database & storage.</strong> Supabase — to store your
            account, business profiles, scans, and preferences.
          </li>
          <li>
            <strong>Reddit data.</strong> Reddit’s public API — read-only,
            for the public posts and comments we surface.
          </li>
          <li>
            <strong>AI inference.</strong> Large-language-model providers
            (currently Groq with Meta Llama) — to score relevance and
            generate rationales. We send only the post/comment text and
            your profile metadata; we do not enable model training on the
            data we send.
          </li>
          <li>
            <strong>Email delivery.</strong> The provider that ships your
            digests and transactional email.
          </li>
          <li>
            <strong>Hosting & monitoring.</strong> Cloud platforms that host
            the Service and help us detect outages.
          </li>
        </ul>
        <p>
          We may also share data when we believe in good faith that doing so
          is necessary to comply with law, protect the rights, property, or
          safety of users or the public, or in connection with a merger,
          acquisition, or asset sale (in which case we’ll give you advance
          notice and your privacy choices will continue to apply).
        </p>
      </Section>

      <Section n={6} id="ai-processing" title="AI processing">
        <p>
          To classify and explain Reddit matches we send three things to our
          LLM provider: (a) the public post/comment we’re evaluating,
          (b) the relevant fields of your business profile, and (c) the
          search plan derived from it. We do not send your account
          credentials, IP address, or activity history outside what is
          necessary for the request.
        </p>
        <p>
          Our LLM providers process this data on a strict no-training basis
          and retain it only for the short period needed to deliver the
          response (or for abuse-monitoring as their terms permit). AI
          output is a best-effort signal, not a fact — see{" "}
          <Link to="/terms#ai">Terms § AI-generated output</Link>.
        </p>
      </Section>

      <Section n={7} id="reddit-data" title="Reddit and public data">
        <p>
          Reddit posts and comments we ingest are public content authored
          by their respective users. We process this content under Reddit’s
          public API terms strictly to operate the Service for you. We do
          not attempt to re-identify pseudonymous Reddit users beyond the
          username displayed on the post.
        </p>
        <p>
          If you are a Reddit user and would like content you’ve authored
          removed from our cache, email us at{" "}
          <a href={`mailto:${PRIVACY_EMAIL}`}>{PRIVACY_EMAIL}</a> with the
          permalink and we’ll remove it from our systems within 30 days.
        </p>
      </Section>

      <Section n={8} id="cookies" title="Cookies and local storage">
        <p>
          We use a small number of cookies and browser-local storage entries
          for things that genuinely need them — keeping you signed in,
          remembering your theme choice, and preserving your in-progress
          onboarding. We do not use third-party advertising cookies and we
          do not track you across other sites.
        </p>
      </Section>

      <Section n={9} id="retention" title="Retention">
        <p>
          We retain your data for as long as your account is active or as
          needed to provide the Service:
        </p>
        <ul>
          <li>
            <strong>Account &amp; profiles.</strong> Until you delete them or
            close your account.
          </li>
          <li>
            <strong>Scans &amp; opportunities.</strong> Until you clear
            results or delete the profile they belong to.
          </li>
          <li>
            <strong>Reddit cache.</strong> Rolling window of approximately
            30 days, after which entries are evicted.
          </li>
          <li>
            <strong>Logs &amp; security data.</strong> Up to 90 days, longer
            if needed to investigate an incident.
          </li>
        </ul>
        <p>
          When you delete your account, we permanently delete or anonymise
          your personal data within 30 days, except where we’re legally
          required to keep it longer.
        </p>
      </Section>

      <Section n={10} id="security" title="Security">
        <p>
          We use industry-standard measures — encryption in transit, access
          controls, scoped service credentials, and regular dependency
          updates — to protect your data. No system is perfectly secure,
          though, so we can’t guarantee absolute security. If we ever learn
          of a security incident affecting your data, we’ll notify you as
          required by law.
        </p>
      </Section>

      <Section n={11} id="your-rights" title="Your rights">
        <p>
          Depending on where you live, you may have the right to:
        </p>
        <ul>
          <li>Access the personal data we hold about you.</li>
          <li>Correct data that is inaccurate or incomplete.</li>
          <li>Delete your data (the “right to be forgotten”).</li>
          <li>Export your data in a portable format.</li>
          <li>
            Object to or restrict certain processing, and withdraw consent
            for processing based on consent.
          </li>
          <li>
            Lodge a complaint with your local data-protection authority.
          </li>
        </ul>
        <p>
          You can exercise most of these rights yourself from the{" "}
          <strong>Settings</strong> page — export your data as JSON, clear
          stored results, or delete your account. For anything you can’t do
          there, email{" "}
          <a href={`mailto:${PRIVACY_EMAIL}`}>{PRIVACY_EMAIL}</a> and we’ll
          respond within 30 days.
        </p>
        <p>
          California residents have additional rights under the CCPA/CPRA,
          including the right to know what categories of personal information
          we collect and to request deletion. We do not “sell” or “share”
          personal information for cross-context behavioural advertising, as
          those terms are defined under the CCPA.
        </p>
      </Section>

      <Section n={12} id="transfers" title="International transfers">
        <p>
          {COMPANY_NAME} is operated from the United States, and the service
          providers we rely on may process data in the United States,
          European Union, and other countries. Where required, we use
          appropriate safeguards — such as the EU Standard Contractual
          Clauses — to protect international transfers of personal data.
        </p>
      </Section>

      <Section n={13} id="children" title="Children">
        <p>
          The Service is not directed at children under 13, and we do not
          knowingly collect personal information from children under 13. If
          you believe a child has provided us with personal information,
          please contact{" "}
          <a href={`mailto:${PRIVACY_EMAIL}`}>{PRIVACY_EMAIL}</a> and we will
          delete it.
        </p>
      </Section>

      <Section n={14} id="changes" title="Changes to this policy">
        <p>
          We may update this Privacy Policy from time to time. If we make
          material changes, we’ll notify you (for example by email or via an
          in-app notice) and update the “Last updated” date at the top of
          this page. We encourage you to review the policy periodically.
        </p>
      </Section>

      <Section n={15} id="contact" title="Contact">
        <p>
          Privacy questions, data requests, or concerns? Reach our team at{" "}
          <a href={`mailto:${PRIVACY_EMAIL}`}>{PRIVACY_EMAIL}</a>. For all
          other inquiries, write to{" "}
          <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a> or read our{" "}
          <Link to="/terms">Terms of Service</Link>.
        </p>
      </Section>
    </LegalLayout>
  );
}

function ContactScreen() {
  const navigate = useNavigate();
  const [email, setEmail] = React.useState("");
  const [message, setMessage] = React.useState("");
  const [status, setStatus] = React.useState("idle"); // idle | sending | success | error
  const [error, setError] = React.useState("");

  React.useEffect(() => {
    if (status !== "success") return undefined;
    const timer = window.setTimeout(() => {
      navigate("/");
    }, 1200);
    return () => window.clearTimeout(timer);
  }, [status, navigate]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!email.trim() || !message.trim()) return;
    setStatus("sending");
    setError("");
    try {
      if (!window.api?.contact?.submit) {
        throw new Error("Contact form is not available right now.");
      }
      await window.api.contact.submit({
        email: email.trim(),
        message: message.trim(),
        source: "contact_page",
      });
      setStatus("success");
      setMessage("");
    } catch (e) {
      setStatus("error");
      setError(e?.message || "Could not send your message.");
    }
  };

  return (
    <div className="legal-page screen-enter" data-kind="contact">
      <Seo
        title="Contact"
        description="Contact Community Ninja with your email and a short message."
      />
      <header className="legal-top">
        <Link to="/" className="legal-brand" aria-label={`${COMPANY_NAME} home`}>
          <span className="legal-brand-name">{COMPANY_NAME}</span>
        </Link>
      </header>

      <main className="contact-page-shell">
        <section className="contact-page-card">
          <div className="contact-page-eyebrow">Contact</div>
          <h1 className="contact-page-title">Send us a note.</h1>
          <p className="contact-page-copy">
            Keep it simple: your email, your message, and we&apos;ll reply from{" "}
            <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>.
          </p>

          <form className="contact-page-form" onSubmit={handleSubmit}>
            <label className="contact-page-field">
              <span>Your email</span>
              <input
                type="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                placeholder="you@example.com"
                autoComplete="email"
                required
              />
            </label>

            <label className="contact-page-field">
              <span>Message</span>
              <textarea
                value={message}
                onChange={(event) => setMessage(event.target.value)}
                placeholder="How can we help?"
                rows={6}
                required
              />
            </label>

            {status === "success" && (
              <div className="contact-page-status is-success">
                Message sent. We&apos;ll follow up at {email.trim()}.
              </div>
            )}
            {status === "error" && (
              <div className="contact-page-status is-error">
                {error || "Could not send your message."}
              </div>
            )}

            <div className="contact-page-actions">
              <Link to="/" className="contact-page-link">
                Back home
              </Link>
              <button
                type="submit"
                className="contact-page-submit"
                disabled={status === "sending" || !email.trim() || !message.trim()}
              >
                {status === "sending" ? "Sending..." : "Send message"}
              </button>
            </div>
          </form>
        </section>
      </main>
    </div>
  );
}

/* ============================================================
   Shared footer — rendered at the bottom of welcome and settings.
   Compact, calm, link-only. Uses Link so navigation stays in the SPA.
   ============================================================ */

function LegalFooter({ variant = "page" }) {
  const year = new Date().getFullYear();
  return (
    <footer className={`legal-page-footer legal-page-footer-${variant}`}>
      <div className="legal-page-footer-inner">
        <div className="legal-page-footer-brand">
          <span>
            © {year} {COMPANY_NAME}
          </span>
        </div>
        <nav
          className="legal-page-footer-links"
          aria-label="Site links"
        >
          <Link to="/how-it-works">How it works</Link>
          <span className="legal-page-footer-sep" aria-hidden="true">·</span>
          <Link to="/why-it-works">Why it works</Link>
          <span className="legal-page-footer-sep" aria-hidden="true">·</span>
          <Link to="/blogs">Blog</Link>
          <span className="legal-page-footer-sep" aria-hidden="true">·</span>
          <Link to="/terms">Terms of Service</Link>
          <span className="legal-page-footer-sep" aria-hidden="true">·</span>
          <Link to="/privacy-policy">Privacy Policy</Link>
          <span className="legal-page-footer-sep" aria-hidden="true">·</span>
          <Link to="/contact">Contact</Link>
        </nav>
      </div>
    </footer>
  );
}

window.TermsScreen = TermsScreen;
window.PrivacyScreen = PrivacyScreen;
window.ContactScreen = ContactScreen;
window.LegalFooter = LegalFooter;
